// src/user/logsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack, TextField, Autocomplete } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export const LogsAdmin = () => {
  const toast = useToast();

  // =========================
  // Estado
  // =========================
  const [logs, setLogs] = useState([]);

  const [adminSel, setAdminSel] = useState(null); // { id, label, raw } | null
  const [filtroEntidad, setFiltroEntidad] = useState("");

  const [loading, setLoading] = useState(true);

  // =========================
  // Headers
  // =========================
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${Global.url}usuarios/admin/logs`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(data?.message || "No se pudieron cargar los logs.");
        setLogs([]);
        return;
      }

      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error cargando logs:", e);
      toast.error("Error de conexión al cargar los logs.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Helpers
  // =========================
  const safeDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const nombreCompleto = (nombre, apellidos) => {
    const nom = String(nombre || "").trim();
    const ape = String(apellidos || "").trim();
    return `${nom} ${ape}`.trim();
  };

  // =========================
  // Opciones (admins) para Autocomplete
  // =========================
  const adminOptions = useMemo(() => {
    // Dedupe por admin_id (puede haber muchos logs del mismo admin)
    const map = new Map();

    (logs || []).forEach((l) => {
      const id = l?.admin_id != null ? Number(l.admin_id) : null;
      if (id == null) return;

      const label =
        nombreCompleto(l.admin_nombre, l.admin_apellidos) ||
        String(l.admin_nombre || "").trim() ||
        `ID ${id}`;

      if (!map.has(id)) map.set(id, { id, label, raw: l });
    });

    return Array.from(map.values()).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [logs]);

  // =========================
  // Rows (filtrado + normalizado)
  // =========================
  const rows = useMemo(() => {
    const filtroAdminId = adminSel?.id != null ? Number(adminSel.id) : null;

    return (logs || [])
      .filter((log) => {
        if (filtroAdminId == null) return true;
        return Number(log.admin_id) === filtroAdminId;
      })
      .filter((log) => {
        if (!filtroEntidad) return true;
        return String(log.entidad || "").toLowerCase().includes(filtroEntidad.toLowerCase());
      })
      .map((log) => {
        const d = safeDate(log.fecha_iso) || safeDate(log.fecha);
        const fecha_txt = d ? d.toLocaleString() : "-";
        const fecha_sort = d ? d.getTime() : 0;

        const admin_nombre_completo =
          nombreCompleto(log.admin_nombre, log.admin_apellidos) ||
          String(log.admin_nombre || "").trim() ||
          (log.admin_id != null ? `ID ${log.admin_id}` : "—");

        const entidadNombre =
          log.entidad === "usuario"
            ? nombreCompleto(log.entidad_nombre, log.entidad_apellidos) ||
              String(log.entidad_nombre || "").trim() ||
              (log.entidad_id != null ? `ID ${log.entidad_id}` : "—")
            : String(log.entidad_nombre || "").trim() ||
              (log.entidad_id != null ? `ID ${log.entidad_id}` : "—");

        const entidad_txt = log.entidad_id
          ? `${log.entidad || "—"} (${entidadNombre})`
          : log.entidad || "—";

        return {
          id: log.id,
          fecha_txt,
          fecha_sort,
          admin_nombre: admin_nombre_completo,
          accion: log.accion || "-",
          entidad_txt,
        };
      });
  }, [logs, adminSel?.id, filtroEntidad]);

  // =========================
  // DataGrid columns
  // =========================
  const columns = useMemo(
    () => [
      {
        field: "fecha_txt",
        headerName: "Fecha",
        minWidth: 190,
        sortComparator: (_v1, _v2, p1, p2) => {
          const t1 = Number(p1?.row?.fecha_sort || 0);
          const t2 = Number(p2?.row?.fecha_sort || 0);
          return t1 - t2;
        },
      },
      { field: "admin_nombre", headerName: "Administrador", flex: 1, minWidth: 280 },
      { field: "accion", headerName: "Acción", flex: 1, minWidth: 220 },
      { field: "entidad_txt", headerName: "Entidad", flex: 1.2, minWidth: 320 },
    ],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="pageContainer appContainer--wide">
      <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Logs de actividad
          </Typography>
          <Typography className="pageHeader__subtitle">
            Registro de acciones realizadas en el sistema (por ahora, principalmente sobre usuarios).
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper className="formPaper">
          <Box className="filtersGrid">
            <Autocomplete
              options={adminOptions}
              value={adminSel}
              onChange={(_, v) => setAdminSel(v)}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              getOptionLabel={(o) => o?.label || ""}
              renderInput={(params) => (
                <TextField {...params} label="Administrador" placeholder="Todos" />
              )}
              clearOnEscape
            />

            <TextField
              label="Entidad"
              value={filtroEntidad}
              onChange={(e) => setFiltroEntidad(e.target.value)}
              placeholder="Ej: usuario…"
            />
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              sorting: { sortModel: [{ field: "fecha_txt", sort: "desc" }] },
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando logs…" : "No hay registros que mostrar."}
                </Box>
              ),
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
};

export default LogsAdmin;
